import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Package, Warning, Clock, Ticket, User, Wrench, MapPin,
  NotePencil, EnvelopeSimple, CheckCircle, ArrowsClockwise,
  Image as ImageIcon, FileText, Star, Check, X
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

const STATUS_LABELS = {
  nuevo: "Nuevo", asignado: "Asignado", en_proceso: "En Proceso",
  esperando_piezas: "Esperando Piezas", esperando_cliente: "Esperando Cliente",
  resuelto: "Resuelto", no_solucionado: "No Solucionado", cancelado: "Cancelado"
};
const STATUS_COLORS = {
  nuevo: "bg-blue-100 text-blue-700 border-blue-300",
  asignado: "bg-indigo-100 text-indigo-700 border-indigo-300",
  en_proceso: "bg-amber-100 text-amber-700 border-amber-300",
  resuelto: "bg-emerald-100 text-emerald-700 border-emerald-300",
  no_solucionado: "bg-red-100 text-red-700 border-red-300",
  cancelado: "bg-slate-100 text-slate-600 border-slate-300",
};
const RESULT_MAP = { resuelto: "RESUELTO", parcial: "PARCIAL", segunda_visita: "2a VISITA", espera_refaccion: "ESPERA REFACCION" };

export default function TicketPublicView() {
  const { linkId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [history, setHistory] = useState([]);
  const [link, setLink] = useState(null);
  const [company, setCompany] = useState({ name: "", logo_path: null });
  const [logoUrl, setLogoUrl] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [surveyQuestions, setSurveyQuestions] = useState([]);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [resolved, setResolved] = useState(null);
  const [answers, setAnswers] = useState({});
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/tickets/share/${linkId}`),
      axios.get(`${API}/tickets/share/${linkId}/survey-config`).catch(() => ({ data: { questions: [] } })),
      axios.get(`${API}/products/config/company/logo-file`, { responseType: "blob" }).catch(() => null),
    ]).then(([ticketRes, surveyRes, logoRes]) => {
      setTicket(ticketRes.data.ticket);
      setHistory(ticketRes.data.history || []);
      setLink(ticketRes.data.link);
      setCompany(ticketRes.data.company || { name: "" });
      setFeedbackSubmitted(!!ticketRes.data.link?.feedback_submitted);
      setSurveyQuestions(surveyRes.data.questions || []);
      if (logoRes?.data) setLogoUrl(URL.createObjectURL(logoRes.data));
    }).catch(e => {
      if (e.response?.status === 410) setError("Este link ha expirado.");
      else if (e.response?.status === 404) setError("Link no encontrado.");
      else setError("Error al cargar el ticket.");
    }).finally(() => setLoading(false));
  }, [linkId]);

  // Auto-refresh
  useEffect(() => {
    if (error) return;
    const iv = setInterval(() => {
      axios.get(`${API}/tickets/share/${linkId}`)
        .then(r => { setTicket(r.data.ticket); setHistory(r.data.history || []); })
        .catch(() => {});
    }, 30000);
    return () => clearInterval(iv);
  }, [linkId, error]);

  const handleSubmitFeedback = async () => {
    if (resolved === null) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/tickets/share/${linkId}/feedback`, { resolved, answers, rating, comment });
      setFeedbackSubmitted(true);
    } catch (e) {
      if (e.response?.status === 409) setFeedbackSubmitted(true);
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center"><Ticket size={48} className="mx-auto text-blue-400 animate-pulse mb-3" /><p className="text-slate-400">Cargando ticket...</p></div>
    </div>
  );
  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center bg-white border rounded-2xl p-10 max-w-md mx-4 shadow-sm">
        <Warning size={48} className="mx-auto text-amber-500 mb-3" />
        <p className="text-lg font-bold text-slate-800 mb-2">{error}</p>
        <p className="text-sm text-slate-400">Contacta al equipo de soporte para un nuevo enlace.</p>
      </div>
    </div>
  );

  const t = ticket;
  const isCampo = t.ticket_type === "campo";
  const statusLabel = STATUS_LABELS[t.status] || t.status;
  const statusColor = STATUS_COLORS[t.status] || "bg-slate-100 text-slate-600 border-slate-300";
  const isActive = !["resuelto", "no_solucionado", "cancelado"].includes(t.status);
  const fmtDate = (d) => { try { return format(new Date(d), "dd MMM yyyy, h:mm a", { locale: es }); } catch { return d || ""; } };
  const contacts = t.contacts || [];
  const pc = contacts[0] || {};

  const SectionHead = ({ num, title }) => (
    <div className="flex items-center gap-2 mb-3">
      <span className="w-5 h-5 rounded bg-[#1E3A5F] text-white flex items-center justify-center text-[9px] font-extrabold">{num}</span>
      <span className="text-xs font-bold text-[#1E3A5F] uppercase tracking-wide">{title}</span>
    </div>
  );
  const Field = ({ label, value }) => value ? (
    <div>
      <p className="text-[9px] font-bold text-slate-400 uppercase">{label}</p>
      <p className="text-sm font-semibold text-slate-800">{value}</p>
    </div>
  ) : null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b px-4 py-3 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          {logoUrl ? <img src={logoUrl} alt="" className="h-8 w-auto" /> : <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-lg flex items-center justify-center text-white text-sm font-bold">{(company.name || "T")[0]}</div>}
          <div>
            <p className="font-bold text-slate-800 text-sm">{company.name || "Soporte Tecnico"}</p>
            <p className="text-[10px] text-slate-400">Seguimiento de Ticket</p>
          </div>
        </div>
        {isActive && <span className="text-[10px] text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full font-semibold flex items-center gap-1"><span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" /> En vivo</span>}
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* ===== TICKET REPORT CARD ===== */}
        <div className="bg-white border rounded-2xl overflow-hidden shadow-sm mb-6">
          {/* Navy header */}
          <div className="bg-gradient-to-r from-[#0F1B2D] to-[#1E3A5F] px-5 py-5 text-white">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-[10px] font-semibold opacity-60 tracking-widest uppercase">{company.name || "SOPORTE TECNICO"}</p>
                <p className="text-xs opacity-50 mt-0.5">SOPORTE TECNICO | INSTALACION | CAPACITACION</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-extrabold">{t.ticket_number}</p>
                <p className="text-[10px] font-bold text-blue-300 tracking-wider">TICKET {isCampo ? "CAMPO" : "OFICINA"}</p>
              </div>
            </div>
          </div>

          {/* Subtitle */}
          <div className="bg-slate-100 px-5 py-2.5 text-center">
            <p className="text-sm font-bold text-[#1E3A5F]">{isCampo ? "Orden de servicio y evidencia de trabajo" : "Resumen operativo del ticket"}</p>
          </div>

          {/* Badges */}
          <div className="grid grid-cols-2 sm:grid-cols-4 border-b">
            <div className="px-4 py-3 bg-slate-50 border-r border-b sm:border-b-0">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Estado</p>
              <span className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${statusColor}`}>
                {isActive && <span className="w-1.5 h-1.5 bg-current rounded-full animate-pulse" />}{statusLabel}
              </span>
            </div>
            <div className="px-4 py-3 bg-slate-50 border-r border-b sm:border-b-0">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Prioridad</p>
              <p className={`text-sm font-bold mt-1 ${t.priority === "critico" || t.priority === "alto" ? "text-red-600" : t.priority === "normal" ? "text-amber-600" : "text-slate-600"}`}>
                {t.priority ? t.priority.charAt(0).toUpperCase() + t.priority.slice(1) : "Normal"}
              </p>
            </div>
            <div className="px-4 py-3 bg-slate-50 border-r">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Ingeniero</p>
              <p className="text-sm font-bold text-slate-800 mt-1">{t.engineer_name || "Sin asignar"}</p>
            </div>
            <div className="px-4 py-3 bg-slate-50">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{isCampo ? "Tipo Servicio" : "Fecha"}</p>
              <p className="text-sm font-bold text-slate-800 mt-1">{isCampo ? (t.service_type || "—") : fmtDate(t.created_at)}</p>
            </div>
          </div>

          {/* Section 1: Client info */}
          <div className="px-5 py-4 border-b">
            <SectionHead num="1" title={isCampo ? "Cliente y Ubicacion" : "Informacion Esencial"} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <Field label="Empresa" value={t.company} />
              {isCampo && <Field label="Ubicacion" value={t.location} />}
              <Field label="Reportado por" value={pc.name ? `${pc.name}${pc.phone ? ` · ${pc.phone}` : ""}` : t.contact_name} />
              {!isCampo && <Field label="Fecha" value={fmtDate(t.created_at)} />}
            </div>
          </div>

          {/* Campo: Section 2 - Work times */}
          {isCampo && (t.arrival_time || t.departure_time) && (
            <div className="px-5 py-4 border-b">
              <SectionHead num="2" title="Registro de Trabajo en Campo" />
              <div className="grid grid-cols-4 gap-3 bg-slate-50 rounded-lg p-3 text-center">
                <div><p className="text-[9px] font-bold text-slate-400 uppercase">Llegada</p><p className="text-sm font-bold text-slate-800">{t.arrival_time || "—"}</p></div>
                <div><p className="text-[9px] font-bold text-slate-400 uppercase">Salida</p><p className="text-sm font-bold text-slate-800">{t.departure_time || "—"}</p></div>
                <div><p className="text-[9px] font-bold text-slate-400 uppercase">Tiempo</p><p className="text-sm font-bold text-amber-600">{t.total_time || "—"}</p></div>
                <div><p className="text-[9px] font-bold text-slate-400 uppercase">KM</p><p className="text-sm font-bold text-slate-800">{t.km_tolls || "—"}</p></div>
              </div>
            </div>
          )}

          {/* Description / Work */}
          <div className="px-5 py-4 border-b">
            <SectionHead num={isCampo ? "3" : "2"} title={isCampo ? "Diagnostico y Acciones Realizadas" : "Detalle Reportado"} />
            <div className="bg-slate-50 border rounded-lg p-3 text-sm text-slate-700 leading-relaxed">
              {t.work_description || t.description || "Sin descripcion"}
            </div>
            {(t.report_channel || t.hardware_type) && (
              <div className="flex flex-wrap gap-3 mt-2 text-[10px] text-slate-500">
                {t.report_channel && <span>Canal: <b>{t.report_channel}</b></span>}
                {t.hardware_type && <span>Equipo: <b>{t.hardware_type}</b></span>}
              </div>
            )}
          </div>

          {/* Campo: Parts */}
          {isCampo && t.parts_used?.length > 0 && (
            <div className="px-5 py-4 border-b">
              <SectionHead num="4" title="Refacciones Utilizadas" />
              <table className="w-full text-sm">
                <thead><tr className="bg-[#1E3A5F] text-white text-[10px] font-bold uppercase">
                  <th className="py-2 px-3 text-left rounded-tl-lg">#</th>
                  <th className="py-2 px-3 text-left">Cant.</th>
                  <th className="py-2 px-3 text-left">Codigo</th>
                  <th className="py-2 px-3 text-left rounded-tr-lg">Descripcion</th>
                </tr></thead>
                <tbody>{t.parts_used.map((p, i) => (
                  <tr key={`part-${i}`} className="border-b">
                    <td className="py-2 px-3 text-slate-400">{i + 1}</td>
                    <td className="py-2 px-3 font-semibold">{p.qty}</td>
                    <td className="py-2 px-3 font-mono text-blue-600 text-xs">{p.code || "—"}</td>
                    <td className="py-2 px-3">{p.description}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}

          {/* Campo: Closure */}
          {isCampo && t.result && (
            <div className="px-5 py-4 border-b">
              <SectionHead num="5" title="Cierre y Conformidad" />
              <div className="flex flex-wrap gap-2 mb-4">
                {["resuelto", "parcial", "segunda_visita", "espera_refaccion"].map(r => (
                  <span key={r} className={`px-3 py-1.5 rounded-lg border text-xs font-bold ${t.result === r ? "border-2 border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-400"}`}>
                    {RESULT_MAP[r] || r}
                  </span>
                ))}
              </div>
              {(t.tech_signature || t.client_signature || t.supervisor_signature) && (
                <div className="grid grid-cols-3 gap-4 text-center pt-2">
                  {t.tech_signature && <div><p className="text-[9px] font-bold text-slate-400 uppercase">Firma Tecnico</p><p className="text-sm font-semibold text-slate-700 mt-1 border-b border-slate-300 pb-1">{t.tech_signature}</p></div>}
                  {t.client_signature && <div><p className="text-[9px] font-bold text-slate-400 uppercase">Firma Cliente</p><p className="text-sm font-semibold text-slate-700 mt-1 border-b border-slate-300 pb-1">{t.client_signature}</p></div>}
                  {t.supervisor_signature && <div><p className="text-[9px] font-bold text-slate-400 uppercase">Vo.Bo. Supervisor</p><p className="text-sm font-semibold text-slate-700 mt-1 border-b border-slate-300 pb-1">{t.supervisor_signature}</p></div>}
                </div>
              )}
            </div>
          )}

          {/* Engineer notes */}
          {t.engineer_comments && (
            <div className="px-5 py-4">
              <SectionHead num={isCampo ? "6" : "3"} title="Solucion de Ingenieria" />
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-slate-700 leading-relaxed">
                <p className="text-[10px] font-bold text-blue-700 uppercase mb-1">Notas de Ingenieria</p>
                {t.engineer_comments}
              </div>
            </div>
          )}
        </div>

        {/* ===== TIMELINE ===== */}
        {history.length > 0 && (
          <div className="bg-white border rounded-2xl px-5 py-6 shadow-sm mb-6">
            <h2 className="text-base font-bold text-slate-800 flex items-center gap-2 mb-5">
              <FileText size={18} className="text-blue-500" /> Historial · {history.length}
            </h2>
            <div className="space-y-1">
              {history.map((entry, idx) => (
                <div key={entry.id || idx} className="flex gap-3 relative pb-4">
                  {idx < history.length - 1 && <div className="absolute left-[15px] top-8 bottom-0 w-0.5 bg-slate-100" />}
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 z-10 ${entry.action === "client_feedback" ? "bg-amber-100 text-amber-600" : entry.action === "created" ? "bg-blue-100 text-blue-600" : "bg-slate-100 text-slate-500"}`}>
                    {entry.action === "client_feedback" ? <Star size={14} weight="fill" /> : entry.action === "comment_added" ? <NotePencil size={14} /> : <FileText size={14} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-800">{entry.details}{entry.user_name && <span className="text-blue-600 ml-1">— {entry.user_name}</span>}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{fmtDate(entry.created_at)}</p>
                    {entry.comment && <div className="mt-1.5 text-xs text-slate-600 bg-slate-50 border rounded-lg p-2.5">{entry.comment}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== CONFORMIDAD + ENCUESTA ===== */}
        {!feedbackSubmitted ? (
          <>
            {/* Conformidad */}
            <div className="bg-white border-2 border-slate-200 rounded-2xl overflow-hidden shadow-sm mb-6">
              <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-5 py-4 text-white">
                <p className="text-sm font-extrabold">Conformidad del Cliente</p>
                <p className="text-[10px] opacity-70 mt-0.5">Indique si el problema fue solucionado</p>
              </div>
              <div className="px-5 py-5">
                <p className="text-sm font-semibold text-slate-700 mb-4">El problema de su equipo fue solucionado?</p>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setResolved(true)} data-testid="feedback-resolved-yes"
                    className={`py-4 rounded-xl border-2 font-bold text-base flex flex-col items-center gap-1 transition-all shadow-sm ${resolved === true ? "border-emerald-500 bg-emerald-100 text-emerald-700 scale-[1.02]" : "border-emerald-300 bg-emerald-50 text-emerald-600 hover:bg-emerald-100"}`}>
                    <Check size={28} weight="bold" /> Si, Resuelto
                  </button>
                  <button onClick={() => setResolved(false)} data-testid="feedback-resolved-no"
                    className={`py-4 rounded-xl border-2 font-bold text-base flex flex-col items-center gap-1 transition-all shadow-sm ${resolved === false ? "border-red-500 bg-red-100 text-red-700 scale-[1.02]" : "border-red-300 bg-red-50 text-red-600 hover:bg-red-100"}`}>
                    <X size={28} weight="bold" /> No Resuelto
                  </button>
                </div>
              </div>
            </div>

            {/* Encuesta */}
            <div className="bg-white border-2 border-blue-200 rounded-2xl overflow-hidden shadow-sm mb-6">
              <div className="bg-gradient-to-r from-blue-700 to-blue-500 px-5 py-4 text-white">
                <p className="text-sm font-extrabold">Encuesta de Satisfaccion</p>
                <p className="text-[10px] opacity-70 mt-0.5">Tu opinion nos ayuda a mejorar</p>
              </div>
              <div className="px-5 py-5 space-y-5">
                {surveyQuestions.map((q, idx) => (
                  <div key={q.id || `q-${idx}`}>
                    <p className="text-sm font-semibold text-slate-700 mb-2">{idx + 1}. {q.text}</p>
                    {q.type === "yesno" && (
                      <div className="flex gap-2">
                        <button onClick={() => setAnswers(a => ({ ...a, [q.text]: true }))} data-testid={`survey-${q.id}-yes`}
                          className={`flex-1 py-2.5 rounded-lg border-2 text-sm font-bold transition-all ${answers[q.text] === true ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}>Si</button>
                        <button onClick={() => setAnswers(a => ({ ...a, [q.text]: false }))} data-testid={`survey-${q.id}-no`}
                          className={`flex-1 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${answers[q.text] === false ? "border-red-400 bg-red-50 text-red-600" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}>No</button>
                      </div>
                    )}
                    {q.type === "rating" && (
                      <div>
                        <div className="flex gap-1 justify-center py-2">
                          {[1, 2, 3, 4, 5].map(s => (
                            <button key={s} onClick={() => setRating(s)} data-testid={`star-${s}`} className="transition-transform hover:scale-110">
                              <Star size={32} weight={s <= rating ? "fill" : "regular"} className={s <= rating ? "text-amber-400" : "text-slate-300"} />
                            </button>
                          ))}
                        </div>
                        {rating > 0 && <p className="text-center text-xs text-slate-400">{rating} de 5 estrellas</p>}
                      </div>
                    )}
                  </div>
                ))}

                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-2">Comentario adicional</p>
                  <textarea value={comment} onChange={(e) => setComment(e.target.value)} data-testid="feedback-comment"
                    className="w-full border border-slate-200 rounded-lg p-3 text-sm text-slate-700 min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="Escriba aqui cualquier observacion o sugerencia..." />
                </div>

                <button onClick={handleSubmitFeedback} disabled={resolved === null || submitting} data-testid="submit-feedback"
                  className="w-full py-3 rounded-xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                  {submitting ? "Enviando..." : "Enviar Encuesta"}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="bg-white border-2 border-emerald-200 rounded-2xl overflow-hidden shadow-sm mb-6 text-center py-10 px-5">
            <CheckCircle size={48} className="mx-auto text-emerald-500 mb-3" />
            <p className="text-lg font-bold text-slate-800">Gracias por tu respuesta!</p>
            <p className="text-sm text-slate-500 mt-1">Tu opinion ha sido registrada y ayuda a mejorar nuestro servicio.</p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-4 space-y-1">
          <p className="text-[9px] text-slate-400 font-semibold">VENTAS 668 397 9434 | INGENIERIA 668 131 7104 - 668 822 6211 | ADMINISTRACION 660 155 1297 | GERENCIA 668 229 75 29</p>
          <p className="text-[10px] text-blue-500 font-bold">WWW.COMPUZONEMX.COM</p>
          {link?.expires_at && <p className="text-[9px] text-slate-400">Link expira: {fmtDate(link.expires_at)}</p>}
        </div>
      </div>
    </div>
  );
}
